/**
 * MarketFit Guided Flow: state-machine-driven prompt → schema flow.
 * Uses sessionId and companyId from query; loads flow config and accumulates state per step.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Card, Button, message, Spin, Alert, Typography, Steps, Collapse, Tooltip, Switch, Radio, Select } from 'antd';
import { ArrowLeftOutlined, CheckOutlined, CheckCircleOutlined, MinusCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { isLocalBridgeApiEnabled } from '../services/bridgeSessionDataAdapter';
import {
  getBridgeSession,
  getGuidedFlowConfig,
  postGuidedFlowExtract,
  postGuidedFlowImport,
  postGuidedFlowScope,
  postGuidedFlowScore,
  postGuidedFlowGenerateIcp,
  type GuidedFlowStateConfig,
  type GuidedSessionState,
  type LocalBridgeSession,
} from '../services/localBridgeSessionApi';
import { canAdvance as checkCanAdvance, getWeakSignals } from '../utils/marketfitGuards';
import {
  createAssumptionsFromExtracted,
  updateAssumptionsValidation,
} from '../utils/marketfitAssumptions';
import { MarketFitAgentChat } from '../components/MarketFit/MarketFitAgentChat';
import { AssumptionLedgerPanel } from '../components/MarketFit/AssumptionLedgerPanel';
import {
  MARKETFIT_INTENT_TYPES,
  CONFIDENCE_BIAS_OPTIONS,
  VALIDATION_DROPDOWN_OPTIONS,
  type MarketFitIntentType,
  type ConfidenceBias,
  type ICPSummary,
} from '../types/marketfitGuided';

const { Text } = Typography;

const INTENT_LABELS: Record<MarketFitIntentType, string> = {
  explore: 'Explore new possible markets',
  validate: 'Validate an existing assumed market',
  prioritize: 'Prioritize known deals',
  diagnose: 'Diagnose weak GTM conversion',
};

const INITIAL_STATE_ID = 'Initialized';

/** Causal chain: Product -> Actor -> Situation -> Value -> Emotion -> Mapping -> Delta -> Commitment -> Score */
const CAUSAL_CHAIN: { id: string; label: string; expectedOutput?: string }[] = [
  { id: 'ProductDefined', label: 'Product', expectedOutput: 'product_belief' },
  { id: 'ActorIdentified', label: 'Actor', expectedOutput: 'actors' },
  { id: 'SituationBounded', label: 'Situation', expectedOutput: 'situation_boundary' },
  { id: 'SituationValued', label: 'Value', expectedOutput: 'situation_value' },
  { id: 'ActorEmotionalBaseline', label: 'Emotion', expectedOutput: 'actor_emotional_baseline' },
  { id: 'ProductSituationMapped', label: 'Mapping', expectedOutput: 'functional_interventions' },
  { id: 'EmotionalDeltaEvaluated', label: 'Delta', expectedOutput: 'emotional_delta' },
  { id: 'CommitmentTested', label: 'Commitment', expectedOutput: 'commitment_decision' },
  { id: 'MarketFitScored', label: 'Score', expectedOutput: undefined },
];

function deepMerge(target: Record<string, unknown>, source: Record<string, unknown>): Record<string, unknown> {
  const result = { ...target };
  for (const k of Object.keys(source)) {
    const sv = source[k];
    const tv = result[k];
    if (sv !== null && typeof sv === 'object' && !Array.isArray(sv) && tv !== null && typeof tv === 'object' && !Array.isArray(tv)) {
      result[k] = deepMerge(tv as Record<string, unknown>, sv as Record<string, unknown>);
    } else {
      result[k] = sv;
    }
  }
  return result;
}

function oneLineSummary(
  stateId: string,
  sessionState: GuidedSessionState,
  expectedOutput?: string
): string | null {
  if (!expectedOutput) return null;
  const val = (sessionState as Record<string, unknown>)[expectedOutput];
  if (val === undefined || val === null) return null;
  if (typeof val === 'string' && val.trim()) return val.length > 60 ? `${val.slice(0, 57)}...` : val;
  if (Array.isArray(val) && val.length > 0) {
    const first = val[0];
    if (typeof first === 'object' && first !== null && 'role' in first) {
      return `Actor: ${(first as Record<string, unknown>).role ?? '—'}`;
    }
    return `${val.length} item(s)`;
  }
  if (typeof val === 'object') {
    const obj = val as Record<string, unknown>;
    const claimed = obj.claimed_effect ?? obj.verb_phrases;
    if (claimed) return String(Array.isArray(claimed) ? claimed[0] : claimed).slice(0, 50);
  }
  return '—';
}

export const MarketFitGuidedFlowPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('sessionId');
  const navigate = useNavigate();

  const [session, setSession] = useState<LocalBridgeSession | null>(null);
  const [config, setConfig] = useState<GuidedFlowStateConfig[]>([]);
  const [currentStateId, setCurrentStateId] = useState<string>(INITIAL_STATE_ID);
  const [sessionState, setSessionState] = useState<GuidedSessionState>({});
  const [userInput, setUserInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [clarificationMessage, setClarificationMessage] = useState<string | null>(null);
  const [failureFlag, setFailureFlag] = useState<string | null>(null);
  const [importedDealId, setImportedDealId] = useState<string | null>(null);
  const [scopeLoading, setScopeLoading] = useState(false);
  const [scopeMissing, setScopeMissing] = useState<{
    missing_situation: string[];
    missing_actors: Array<{ actor_index: number; missing: string[] }>;
  } | null>(null);
  const [guardBlockReason, setGuardBlockReason] = useState<string | null>(null);
  const [productLeakageWarning, setProductLeakageWarning] = useState(false);
  const [sessionAnalytics, setSessionAnalytics] = useState({
    clarificationCount: 0,
    leakageWarnings: 0,
    softGuardWarnings: 0,
  });
  const [sessionSnapshotAtCommit, setSessionSnapshotAtCommit] = useState<GuidedSessionState | null>(null);
  const [operatorOverride, setOperatorOverride] = useState(false);
  const [intentType, setIntentType] = useState<MarketFitIntentType>('explore');
  const [confidenceBias, setConfidenceBias] = useState<ConfidenceBias>('neutral');
  const [scoreLoading, setScoreLoading] = useState(false);
  const [icpLoading, setIcpLoading] = useState(false);

  const loadSessionAndConfig = useCallback(async () => {
    if (!sessionId || !isLocalBridgeApiEnabled()) {
      setSession(null);
      setConfig([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [s, flowConfig] = await Promise.all([getBridgeSession(sessionId), getGuidedFlowConfig()]);
      setSession(s ?? null);
      setConfig(flowConfig);
    } catch {
      setSession(null);
      setConfig([]);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    loadSessionAndConfig();
  }, [loadSessionAndConfig]);

  // Freeze session snapshot once when entering CommitmentTested
  const hasFrozenAtCommit = React.useRef(false);
  useEffect(() => {
    if (currentStateId === 'CommitmentTested' && !hasFrozenAtCommit.current && Object.keys(sessionState).length > 0) {
      setSessionSnapshotAtCommit(JSON.parse(JSON.stringify(sessionState)));
      hasFrozenAtCommit.current = true;
    }
    if (currentStateId !== 'CommitmentTested') hasFrozenAtCommit.current = false;
  }, [currentStateId, sessionState]);

  // When entering EnvironmentScoped, run scope check; if complete advance, else show missing
  useEffect(() => {
    if (currentStateId !== 'EnvironmentScoped' || !config.length) {
      if (currentStateId !== 'EnvironmentScoped') setScopeMissing(null);
      return;
    }
    let cancelled = false;
    setScopeMissing(null);
    setScopeLoading(true);
    postGuidedFlowScope(sessionState).then((res) => {
      if (cancelled) return;
      setScopeLoading(false);
      if (!res) return;
      if (res.complete && res.deal_environment) {
        setSessionState((prev) => ({ ...prev, deal_environment: res.deal_environment as unknown as Record<string, unknown> }));
        setCurrentStateId('ProductSituationMapped');
      } else {
        setScopeMissing({
          missing_situation: res.missing_situation || [],
          missing_actors: res.missing_actors || [],
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [currentStateId, config.length, sessionState]);

  // When entering MarketFitScored, fetch score and store in marketfit_scored_output (only if not already loaded)
  useEffect(() => {
    if (currentStateId !== 'MarketFitScored') return;
    if (sessionState.marketfit_scored_output?.score != null && !scoreLoading) return;
    let cancelled = false;
    setScoreLoading(true);
    postGuidedFlowScore(sessionState).then((res) => {
      if (cancelled) return;
      setScoreLoading(false);
      if (!res) return;
      setSessionState((prev) => ({
        ...prev,
        marketfit_scored_output: {
          score: res.marketfit_score,
          interpretation_band: res.interpretation?.band,
          interpretation_label: res.interpretation?.label,
          confidence_weight: res.confidence_weight,
          weighted_marketfit_score: res.weighted_marketfit_score,
          svp: res.svp,
          acr: res.acr,
          ce: res.ce,
          icp_summary: prev.marketfit_scored_output?.icp_summary ?? null,
        },
      }));
    });
    return () => {
      cancelled = true;
    };
  }, [currentStateId, sessionState]);

  const currentConfig = config.find((c) => c.id === currentStateId);
  const currentStepIndex = config.findIndex((c) => c.id === currentStateId);
  const isTerminal = currentStateId === 'Completed' || currentStateId === 'BridgeOptionsSelected';
  const needsUserText =
    currentConfig?.user_prompt &&
    currentStateId !== 'EnvironmentScoped' &&
    currentStateId !== 'MarketFitScored' &&
    currentStateId !== 'BridgeOptionsSelected' &&
    currentStateId !== 'Completed';
  const showChat =
    (needsUserText || currentConfig?.expected_output) &&
    currentStateId !== 'Initialized' &&
    currentStateId !== 'BridgeOptionsSelected' &&
    currentStateId !== 'Completed' &&
    currentStateId !== 'MarketFitScored' &&
    currentStateId !== 'EnvironmentScoped';

  const ASSUMPTION_OUTPUTS = ['situation_boundary', 'situation_value', 'actors', 'emotional_delta', 'commitment_decision'];

  const mergeExtracted = useCallback((
    expectedOutput: string | null,
    extracted: Record<string, unknown>,
    options?: { merge?: boolean; source?: 'user_stated' | 'agent_hypothesis' }
  ) => {
    if (!expectedOutput) return;
    const source = options?.source ?? 'agent_hypothesis';
    setSessionState((prev) => {
      const next = { ...prev };
      const incoming = extracted[expectedOutput] !== undefined ? extracted[expectedOutput] : extracted;
      const existing = (prev as Record<string, unknown>)[expectedOutput];
      let value: unknown;
      if (options?.merge && typeof incoming === 'object' && incoming !== null && !Array.isArray(incoming) && typeof existing === 'object' && existing !== null && !Array.isArray(existing)) {
        value = deepMerge(existing as Record<string, unknown>, incoming as Record<string, unknown>);
      } else {
        value = incoming;
      }
      (next as Record<string, unknown>)[expectedOutput] = value;
      if (expectedOutput === 'functional_interventions' && extracted.asset_interactions !== undefined) {
        next.asset_interactions = extracted.asset_interactions as Record<string, unknown>[];
      }
      if (ASSUMPTION_OUTPUTS.includes(expectedOutput)) {
        const newAssumptions = createAssumptionsFromExtracted(expectedOutput, extracted, source, currentStateId);
        next.assumptions = [...(prev.assumptions ?? []), ...newAssumptions];
      }
      return next;
    });
  }, [currentStateId]);

  const handleSubmit = async () => {
    if (!currentConfig) return;
    setSubmitting(true);
    setClarificationMessage(null);
    setFailureFlag(null);
    setGuardBlockReason(null);
    setProductLeakageWarning(false);
    try {
      const res = await postGuidedFlowExtract(currentStateId, userInput, sessionState);
      if (!res) {
        message.error('Failed to submit');
        return;
      }
      const mergedState: GuidedSessionState = { ...sessionState };
      if (currentConfig.expected_output && res.extracted) {
        const value = res.extracted[currentConfig.expected_output] !== undefined
          ? res.extracted[currentConfig.expected_output]
          : res.extracted;
        (mergedState as Record<string, unknown>)[currentConfig.expected_output] = value;
        if (currentConfig.expected_output === 'functional_interventions' && res.extracted.asset_interactions !== undefined) {
          mergedState.asset_interactions = res.extracted.asset_interactions as Record<string, unknown>[];
        }
        if (['situation_boundary', 'situation_value', 'actors', 'emotional_delta', 'commitment_decision'].includes(currentConfig.expected_output)) {
          const newAssumptions = createAssumptionsFromExtracted(currentConfig.expected_output, res.extracted, 'user_stated', currentStateId);
          mergedState.assumptions = [...(mergedState.assumptions ?? []), ...newAssumptions];
        }
      }
      setSessionState(mergedState);

      if (res.gate_result === 'proceed' && res.next_state_id) {
        const guardResult = checkCanAdvance(currentStateId, mergedState as Record<string, unknown>);
        if (!guardResult.canAdvance) {
          setGuardBlockReason(guardResult.reason ?? 'Guards not satisfied');
          if (guardResult.productLeakageDetected) {
            setProductLeakageWarning(true);
            setSessionAnalytics((a) => ({ ...a, leakageWarnings: a.leakageWarnings + 1 }));
          }
          if (guardResult.crossStateWarning) {
            setSessionAnalytics((a) => ({ ...a, softGuardWarnings: a.softGuardWarnings + 1 }));
          }
          return;
        }
        if (guardResult.crossStateWarning) {
          setSessionAnalytics((a) => ({ ...a, softGuardWarnings: a.softGuardWarnings + 1 }));
        }
        if (res.next_state_id === 'MarketFitScored' || res.next_state_id === 'Completed') {
          const toLog = { ...sessionAnalytics };
          if (guardResult.crossStateWarning) toLog.softGuardWarnings += 1;
          console.debug('[MarketFit]', toLog);
        }
        setCurrentStateId(res.next_state_id);
        setUserInput('');
      }
      if (res.gate_result === 'clarify' && res.clarification_message) {
        setClarificationMessage(res.clarification_message);
        setSessionAnalytics((a) => ({ ...a, clarificationCount: a.clarificationCount + 1 }));
      }
      if (res.gate_result === 'flag' && res.failure_flag) {
        setFailureFlag(res.failure_flag);
        if (res.next_state_id) {
          setCurrentStateId(res.next_state_id);
          setUserInput('');
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    if (!currentConfig?.next_state_id) return;
    const guardResult = checkCanAdvance(currentStateId, sessionState as Record<string, unknown>);
    if (!operatorOverride && !guardResult.canAdvance) {
      setGuardBlockReason(guardResult.reason ?? 'Guards not satisfied');
      if (guardResult.productLeakageDetected) {
        setProductLeakageWarning(true);
        setSessionAnalytics((a) => ({ ...a, leakageWarnings: a.leakageWarnings + 1 }));
      }
      if (guardResult.crossStateWarning) {
        setSessionAnalytics((a) => ({ ...a, softGuardWarnings: a.softGuardWarnings + 1 }));
      }
      return;
    }
    if (guardResult.crossStateWarning) {
      setSessionAnalytics((a) => ({ ...a, softGuardWarnings: a.softGuardWarnings + 1 }));
    }
    setGuardBlockReason(null);
    setProductLeakageWarning(false);
    if (currentConfig.next_state_id === 'MarketFitScored' || currentConfig.next_state_id === 'Completed') {
      const toLog = { ...sessionAnalytics };
      if (guardResult.crossStateWarning) toLog.softGuardWarnings += 1;
      console.debug('[MarketFit]', toLog);
    }
    setCurrentStateId(currentConfig.next_state_id);
  };

  const handleBridgeOption = (option: 'reframe_situation' | 'change_actor' | 'modify_product' | 'complete') => {
    if (option === 'complete') {
      setCurrentStateId('Completed');
      return;
    }
    const target: Record<string, string> = {
      reframe_situation: 'SituationBounded',
      change_actor: 'ActorIdentified',
      modify_product: 'ProductDefined',
    };
    if (target[option]) setCurrentStateId(target[option]);
  };

  const handleImport = async () => {
    if (!session?.company_id || !sessionId) return;
    setSubmitting(true);
    try {
      const res = await postGuidedFlowImport(sessionId, session.company_id, sessionState);
      if (res) {
        setImportedDealId(res.deal_id);
        message.success('Imported to MarketFit');
        setCurrentStateId('Completed');
      } else {
        message.error('Import failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const goToDeal = () => {
    if (importedDealId && sessionId) {
      navigate(`/local-bridge/marketfit?sessionId=${sessionId}&dealId=${importedDealId}`);
    }
  };

  const handleApplyFromChat = useCallback(
    (data: Record<string, unknown>, options?: { merge?: boolean }) => {
      if (currentConfig?.expected_output) {
        mergeExtracted(currentConfig.expected_output, data, { ...options, source: 'agent_hypothesis' });
      }
    },
    [currentConfig?.expected_output, mergeExtracted]
  );

  /** Derive required fields for persona from expected_output (per plan Phase 1) */
  const derivedRequiredFields = currentConfig?.expected_output
    ? ((): string[] => {
        const map: Record<string, string[]> = {
          product_belief: ['claimed_effect'],
          actors: ['role', 'authority'],
          situation_boundary: ['trigger_type', 'constraint'],
          situation_value: ['cost', 'friction'],
          actor_emotional_baseline: ['emotional_friction'],
          functional_interventions: ['constraint_interaction'],
          emotional_delta: ['improvement_stated'],
          commitment_decision: ['explicit_decision'],
        };
        return map[currentConfig.expected_output!] ?? [currentConfig.expected_output];
      })()
    : undefined;

  const isChainCompleted = (stateId: string) => {
    const stateIdx = config.findIndex((c) => c.id === stateId);
    return stateIdx >= 0 && currentStepIndex > stateIdx;
  };

  const currentGuardResult = checkCanAdvance(currentStateId, sessionState as Record<string, unknown>);

  if (!isLocalBridgeApiEnabled()) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="info" message="Local Bridge API not configured" />
        <Link to="/local-bridge">
          <Button icon={<ArrowLeftOutlined />} style={{ marginTop: 16 }}>
            Back to Local Bridge
          </Button>
        </Link>
      </div>
    );
  }

  if (!sessionId) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="warning" message="No session selected" description="Add ?sessionId=... to the URL." />
        <Link to="/local-bridge">
          <Button icon={<ArrowLeftOutlined />} style={{ marginTop: 16 }}>
            Back to Local Bridge
          </Button>
        </Link>
      </div>
    );
  }

  if (loading || !config.length) {
    return (
      <div style={{ padding: 24 }}>
        <Spin tip="Loading flow..." />
      </div>
    );
  }

  return (
    <div style={{ padding: 24, display: 'flex', gap: 24, maxWidth: 1200, margin: '0 auto', flexWrap: 'wrap' }}>
      <div style={{ flex: '0 0 200px', minWidth: 180, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Collapse
        defaultActiveKey={['chain']}
        items={[
          {
            key: 'chain',
            label: 'Causal chain',
            children: (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {CAUSAL_CHAIN.map(({ id, label, expectedOutput }) => {
                  const completed = isChainCompleted(id);
                  const current = id === currentStateId;
                  const summary = completed ? oneLineSummary(id, sessionState, expectedOutput) : null;
                  const weakSignals = completed ? getWeakSignals(id, sessionState as Record<string, unknown>) : [];
                  return (
                    <div
                      key={id}
                      style={{
                        padding: '4px 8px',
                        borderRadius: 4,
                        background: current ? '#e6f7ff' : 'transparent',
                        borderLeft: current ? '3px solid #1890ff' : '3px solid transparent',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {completed ? (
                          <CheckCircleOutlined style={{ color: '#52c41a', flexShrink: 0 }} />
                        ) : (
                          <MinusCircleOutlined style={{ color: '#d9d9d9', flexShrink: 0 }} />
                        )}
                        <Text strong={current} style={{ fontSize: 12 }}>
                          {label}
                        </Text>
                        {weakSignals.length > 0 && (
                          <Tooltip title={weakSignals.join('; ')}>
                            <WarningOutlined style={{ color: '#faad14', flexShrink: 0, marginLeft: 4 }} />
                          </Tooltip>
                        )}
                      </div>
                      {summary && (
                        <Text type="secondary" style={{ fontSize: 11, marginLeft: 20, display: 'block' }} ellipsis>
                          {summary}
                        </Text>
                      )}
                    </div>
                  );
                })}
              </div>
            ),
          },
        ]}
      />
      <AssumptionLedgerPanel assumptions={sessionState.assumptions} />
      </div>
      <div style={{ flex: '1 1 400px', minWidth: 0 }}>
      <Link to={`/local-bridge/marketfit?sessionId=${sessionId}`}>
        <Button icon={<ArrowLeftOutlined />} style={{ marginBottom: 16 }}>
          Back to MarketFit
        </Button>
      </Link>

      <Typography.Title level={4}>MarketFit Guided Session</Typography.Title>
      <Steps
        current={currentStepIndex >= 0 ? currentStepIndex : 0}
        size="small"
        style={{ marginBottom: 24 }}
        items={config.slice(0, -1).map((s) => ({ title: s.title, icon: s.id === currentStateId ? undefined : undefined }))}
      />

      <Card title={currentConfig?.title ?? currentStateId}>
        {currentConfig?.user_prompt && (
          <div style={{ marginBottom: 16 }}>
            <Text>{currentConfig.user_prompt}</Text>
          </div>
        )}

        {clarificationMessage && (
          <Alert type="info" message={clarificationMessage} style={{ marginBottom: 16 }} />
        )}
        {guardBlockReason && (
          <Alert
            type={productLeakageWarning ? 'error' : 'warning'}
            message={productLeakageWarning ? 'Product leakage detected' : 'Cannot advance'}
            description={guardBlockReason}
            style={{ marginBottom: 16 }}
          />
        )}
        {failureFlag && (
          <Alert type="warning" message={`Flag: ${failureFlag}`} style={{ marginBottom: 16 }} />
        )}
        {currentGuardResult.crossStateWarning && (
          <Alert type="warning" message={currentGuardResult.crossStateWarning} style={{ marginBottom: 16 }} />
        )}
        {operatorOverride && !currentGuardResult.canAdvance && currentGuardResult.reason && (
          <Alert type="info" message="Override active" description={currentGuardResult.reason} style={{ marginBottom: 16 }} />
        )}

        {currentStateId === 'EnvironmentScoped' && (
          <div style={{ marginBottom: 16 }}>
            {scopeLoading && <Spin tip="Checking situation and actor completeness..." />}
            {!scopeLoading && scopeMissing && (
              <>
                <Alert
                  type="warning"
                  message="Missing scoping dimensions"
                  description="Add the required situation and actor dimensions so we can derive the deal environment. Then return to this step."
                  style={{ marginBottom: 16 }}
                />
                {scopeMissing.missing_situation.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <Text strong>Missing situation dimensions:</Text>
                    <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                      {scopeMissing.missing_situation.map((key) => {
                        const hints: Record<string, string> = {
                          trigger_type: 'continuous | scheduled | stochastic | conditional',
                          demand_scope: 'local | regional | national | global',
                          execution_scope: 'local | mobile | centralized',
                          value_realization_location: 'on_site | distributed | abstract',
                          temporal_profile: 'predictable | mixed | volatile',
                        };
                        return (
                          <li key={key}>
                            <Text strong>{key}</Text>
                            {hints[key] && <Text type="secondary"> — {hints[key]}</Text>}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
                {scopeMissing.missing_actors.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <Text strong>Missing actor dimensions:</Text>
                    <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                      {scopeMissing.missing_actors.map(({ actor_index, missing }) => (
                        <li key={actor_index}>
                          <Text strong>Actor {actor_index}</Text>
                          <Text type="secondary">: missing {missing.join(', ')}</Text>
                          <Text type="secondary"> (role, commitment_type, authority_scope, substitutability)</Text>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Button onClick={() => { setScopeMissing(null); setCurrentStateId('SituationValued'); }}>
                    Go back to Situation value
                  </Button>
                  <Button onClick={() => { setScopeMissing(null); setCurrentStateId('ActorIdentified'); }}>
                    Go back to Actors
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {currentStateId === 'BridgeOptionsSelected' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Button onClick={() => handleBridgeOption('reframe_situation')}>Reframe situation</Button>
            <Button onClick={() => handleBridgeOption('change_actor')}>Change actor</Button>
            <Button onClick={() => handleBridgeOption('modify_product')}>Modify product</Button>
            <Button type="primary" icon={<CheckOutlined />} onClick={() => handleBridgeOption('complete')}>
              Complete
            </Button>
            <Button onClick={handleImport} loading={submitting}>
              Import to MarketFit (create deal)
            </Button>
          </div>
        )}

        {currentStateId === 'MarketFitScored' && (
          <div>
            {scoreLoading && <Spin tip="Computing scores..." />}
            {!scoreLoading && sessionState.marketfit_scored_output && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12, marginBottom: 12 }}>
                  <div>
                    <Text type="secondary" style={{ fontSize: 11 }}>Raw Score</Text>
                    <div style={{ fontSize: 18, fontWeight: 600 }}>{sessionState.marketfit_scored_output.score?.toFixed(1) ?? '—'}</div>
                  </div>
                  <div>
                    <Text type="secondary" style={{ fontSize: 11 }}>Confidence Weight</Text>
                    <div style={{ fontSize: 18, fontWeight: 600 }}>{(sessionState.marketfit_scored_output.confidence_weight ?? 1).toFixed(2)}</div>
                  </div>
                  <div>
                    <Text type="secondary" style={{ fontSize: 11 }}>Weighted Score</Text>
                    <div style={{ fontSize: 18, fontWeight: 600 }}>{sessionState.marketfit_scored_output.weighted_marketfit_score?.toFixed(1) ?? '—'}</div>
                  </div>
                  <div>
                    <Text type="secondary" style={{ fontSize: 11 }}>Interpretation</Text>
                    <div style={{ fontSize: 12 }}>{sessionState.marketfit_scored_output.interpretation_label ?? '—'}</div>
                  </div>
                </div>
                <div style={{ fontSize: 11, color: '#666', marginBottom: 16 }}>
                  SVP: {sessionState.marketfit_scored_output.svp?.toFixed(2) ?? '—'} · ACR: {sessionState.marketfit_scored_output.acr?.toFixed(2) ?? '—'} · CE: {sessionState.marketfit_scored_output.ce?.toFixed(2) ?? '—'}
                </div>
                <div style={{ marginBottom: 16 }}>
                  <Button
                    loading={icpLoading}
                    onClick={async () => {
                      setIcpLoading(true);
                      const res = await postGuidedFlowGenerateIcp(sessionState);
                      setIcpLoading(false);
                      if (res?.icp_summary) {
                        setSessionState((prev) => ({
                          ...prev,
                          marketfit_scored_output: {
                            ...prev.marketfit_scored_output,
                            icp_summary: res.icp_summary as unknown as ICPSummary,
                          },
                        }));
                      } else if (res?.reason) {
                        message.warning(`Cannot generate ICP: ${res.reason}`);
                      }
                    }}
                  >
                    Generate ICP
                  </Button>
                </div>
                {sessionState.marketfit_scored_output.icp_summary && (
                  <div style={{ padding: 12, background: '#fafafa', borderRadius: 8, marginBottom: 16 }}>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>ICP Summary</Text>
                    <div style={{ fontSize: 12 }}>
                      <div><Text type="secondary">Primary actor:</Text> {sessionState.marketfit_scored_output.icp_summary.primary_actor?.role} · {sessionState.marketfit_scored_output.icp_summary.primary_actor?.authority_scope}</div>
                      <div><Text type="secondary">Emotional drivers:</Text> {Array.isArray(sessionState.marketfit_scored_output.icp_summary.primary_actor?.emotional_drivers) ? sessionState.marketfit_scored_output.icp_summary.primary_actor.emotional_drivers.join(', ') : '—'}</div>
                      <div><Text type="secondary">Firmographic:</Text> {sessionState.marketfit_scored_output.icp_summary.firmographic_filters?.size_range} · {sessionState.marketfit_scored_output.icp_summary.firmographic_filters?.operational_trigger}</div>
                      <div><Text type="secondary">Messaging:</Text> {sessionState.marketfit_scored_output.icp_summary.messaging?.constraint_framing}</div>
                    </div>
                  </div>
                )}
              </div>
            )}
            <div style={{ marginTop: 16 }}>
              <Button type="primary" onClick={() => setCurrentStateId('BridgeOptionsSelected')}>
                Continue to bridge options
              </Button>
            </div>
          </div>
        )}

        {currentStateId === 'Completed' && (
          <div>
            {importedDealId ? (
              <>
                <Text>Deal created. You can open it from the MarketFit page.</Text>
                <div style={{ marginTop: 16 }}>
                  <Button type="primary" onClick={goToDeal}>
                    Open deal
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Text>Session complete. Import to create a MarketFit deal.</Text>
                <div style={{ marginTop: 16 }}>
                  <Button type="primary" onClick={handleImport} loading={submitting}>
                    Import to MarketFit
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {(() => {
          const assumptionsFromStep = (sessionState.assumptions ?? []).filter((a) => a.source_step === currentStateId);
          const showValidationDropdown =
            assumptionsFromStep.length > 0 &&
            currentConfig?.expected_output &&
            ['situation_boundary', 'situation_value', 'actors', 'emotional_delta', 'commitment_decision'].includes(currentConfig.expected_output);
          return showValidationDropdown ? (
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Validation for this step</Text>
              <Select
                placeholder="How is this validated?"
                style={{ width: 220 }}
                allowClear
                options={VALIDATION_DROPDOWN_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                onChange={(val) => {
                  if (!val) return;
                  const opt = VALIDATION_DROPDOWN_OPTIONS.find((o) => o.value === val);
                  if (!opt) return;
                  setSessionState((prev) => ({
                    ...prev,
                    assumptions: updateAssumptionsValidation(
                      prev.assumptions ?? [],
                      currentStateId,
                      opt.value,
                      opt.confidence
                    ),
                  }));
                }}
              />
            </div>
          ) : null;
        })()}

        {needsUserText && (
          <>
            <textarea
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              placeholder="Your response..."
              rows={4}
              style={{ width: '100%', marginBottom: 16, resize: 'vertical' }}
            />
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button type="primary" onClick={handleSubmit} loading={submitting}>
                Submit
              </Button>
              {currentConfig?.expected_output && (
                <>
                  <Button
                    onClick={handleNext}
                    disabled={!operatorOverride && !currentGuardResult.canAdvance}
                    title={!operatorOverride && !currentGuardResult.canAdvance ? currentGuardResult.reason : undefined}
                  >
                    Next
                  </Button>
                  <span style={{ marginLeft: 8 }}>
                    <Switch
                      size="small"
                      checked={operatorOverride}
                      onChange={setOperatorOverride}
                    />
                    <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>Override guard and advance</Text>
                  </span>
                </>
              )}
            </div>
          </>
        )}

        {currentStateId === 'Initialized' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>What is your primary intent for this session?</Text>
              <Radio.Group
                value={intentType}
                onChange={(e) => setIntentType(e.target.value)}
                style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
              >
                {MARKETFIT_INTENT_TYPES.map((t) => (
                  <Radio key={t} value={t}>
                    {INTENT_LABELS[t]}
                  </Radio>
                ))}
              </Radio.Group>
            </div>
            <div>
              <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Confidence bias (optional)</Text>
              <Select
                value={confidenceBias}
                onChange={(v) => setConfidenceBias(v)}
                style={{ width: 180 }}
                options={CONFIDENCE_BIAS_OPTIONS.map((b) => ({
                  value: b,
                  label: b.charAt(0).toUpperCase() + b.slice(1),
                }))}
              />
            </div>
            <Button
              type="primary"
              onClick={() => {
                setSessionState((prev) => ({
                  ...prev,
                  marketfit_intent: { type: intentType, confidence_bias: confidenceBias },
                }));
                setCurrentStateId('ProductDefined');
              }}
            >
              Start
            </Button>
          </div>
        )}
      </Card>
      </div>

      {showChat && session?.company_id && sessionId && (
        <div style={{ flex: '0 0 380px', minWidth: 320, maxWidth: 420 }}>
          <MarketFitAgentChat
            artifactType="guided"
            artifactId={null}
            companyId={session.company_id}
            flowContext={{ sessionId, companyId: session.company_id }}
            stateId={currentStateId}
            requiredFields={derivedRequiredFields}
            guardRules={currentConfig?.guards}
            sessionSnapshot={sessionState as Record<string, unknown>}
            expectedOutput={currentConfig?.expected_output ?? undefined}
            onApplyToForm={handleApplyFromChat}
            userPrompt={currentConfig?.user_prompt}
            formData={sessionState as Record<string, unknown>}
          />
        </div>
      )}
    </div>
  );
};
